import { useMemo, useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { ArrowDown, ArrowUp, ArrowUpDown, Pencil, Plus, Trash2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Modal } from '@/components/ui/Modal'
import { QuizQuestionForm } from '@/components/quizzes/QuizQuestionForm'
import {
  useQuiz,
  useQuizQuestions,
  useCreateQuizQuestion,
  useUpdateQuizQuestion,
  useDeleteQuizQuestion,
} from '@/hooks/useQuizzes'
import { QuizQuestion } from '@/types/quizzes'
import { paths } from '@/routes/path'
import { useCoursesByIds } from '@/hooks/useCourses'
import { QuizModel } from '@/models/quiz.model'
import { labelForQuizQuestionType } from '@/utils/quiz'
import BackButton from '@/components/ui/BackButton'

export function QuizDetailPage() {
  const { quizId } = useParams<{ quizId: string }>()
  const navigate = useNavigate()

  const { data: quiz, isLoading: isQuizLoading, error: quizError } = useQuiz(quizId)
  const { data: quizQuestions, isLoading: isQuestionsLoading } = useQuizQuestions(quizId)

  const createQuestion = useCreateQuizQuestion(quizId)
  const updateQuestion = useUpdateQuizQuestion(quizId)
  const reorderQuestion = useUpdateQuizQuestion(quizId)
  const deleteQuestion = useDeleteQuizQuestion(quizId)

  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false)
  const [questionToEdit, setQuestionToEdit] = useState<QuizQuestion | null>(null)
  const [questionToDelete, setQuestionToDelete] = useState<QuizQuestion | null>(null)
  const [orderedQuestions, setOrderedQuestions] = useState<QuizQuestion[]>([])
  const [isReorderModalOpen, setIsReorderModalOpen] = useState(false)
  const [reorderDraft, setReorderDraft] = useState<QuizQuestion[]>([])
  const [isSavingOrder, setIsSavingOrder] = useState(false)

  const courseId = quiz?.courseId ?? ''
  const { data: courseLookup } = useCoursesByIds(courseId ? [courseId] : undefined)
  const course = courseId ? courseLookup?.[courseId] : undefined

  const questions = quizQuestions?.questions ?? quiz?.questions ?? []
  const normalizedQuestions = useMemo(() => normalizeQuestionOrder(questions), [questions])

  useEffect(() => {
    setOrderedQuestions((current) => {
      if (areQuestionsInSameOrder(current, normalizedQuestions)) {
        return current
      }
      return normalizedQuestions
    })
  }, [normalizedQuestions])

  const displayedQuestions = orderedQuestions.length ? orderedQuestions : normalizedQuestions

  const totalPoints = useMemo(
    () =>
      displayedQuestions.reduce((sum, question) => sum + (Number(question.scoreWeight ?? 0) || 0), 0),
    [displayedQuestions],
  )

  const handleCreateQuestion = () => {
    setQuestionToEdit(null)
    setIsQuestionModalOpen(true)
  }

  const handleEditQuestion = (question: QuizQuestion) => {
    setQuestionToEdit(question)
    setIsQuestionModalOpen(true)
  }

  const handleSubmitQuestion = async (payload: any) => {
    if (!quizId) return
    if (questionToEdit) {
      await updateQuestion.mutateAsync({ ...payload, id: questionToEdit.id, type: questionToEdit.type })
      toast.success('Question updated.')
    } else {
      const highestOrder = displayedQuestions.reduce((max, question, index) => {
        const current = question.order ?? index + 1
        return current > max ? current : max
      }, 0)
      await createQuestion.mutateAsync({
        ...payload,
        order: highestOrder + 1,
      })
      toast.success('Question added.')
    }
    setIsQuestionModalOpen(false)
    setQuestionToEdit(null)
  }

  const handleDeleteQuestion = async () => {
    if (!questionToDelete) return
    await deleteQuestion.mutateAsync(questionToDelete.id)
    toast.success('Question removed.')
    setQuestionToDelete(null)
  }

  const persistQuestionOrder = useCallback(
    async (nextOrder: QuizQuestion[]): Promise<boolean> => {
      if (!quizId || nextOrder.length === 0) return false
      const updates = nextOrder
        .map((question, index) => ({
          question,
          order: index + 1,
          previousOrder: orderedQuestions.find((item) => item.id === question.id)?.order ?? index + 1,
        }))
        .filter((entry) => entry.previousOrder !== entry.order)

      if (updates.length === 0) {
        setOrderedQuestions(applySequentialOrder(nextOrder))
        toast.info('Question order already up to date.')
        return true
      }

      try {
        setIsSavingOrder(true)
        for (const entry of updates) {
          await reorderQuestion.mutateAsync({
            id: entry.question.id,
            type: entry.question.type,
            order: entry.order,
          })
        }
        setOrderedQuestions(applySequentialOrder(nextOrder))
        toast.success('Question order updated.')
        return true
      } catch (error) {
        console.error(error)
        toast.error('Unable to update question order. Please try again.')
        setOrderedQuestions(normalizedQuestions)
        return false
      } finally {
        setIsSavingOrder(false)
      }
    },
    [normalizedQuestions, orderedQuestions, quizId, reorderQuestion],
  )

  const handleOpenReorderModal = () => {
    if (displayedQuestions.length === 0) {
      toast.info('Add at least one question before reordering.')
      return
    }
    setReorderDraft(applySequentialOrder(displayedQuestions))
    setIsReorderModalOpen(true)
  }

  const handleCloseReorderModal = () => {
    if (isSavingOrder) return
    setIsReorderModalOpen(false)
    setReorderDraft([])
  }

  const handleMoveDraftQuestion = (fromIndex: number, toIndex: number) => {
    setReorderDraft((current) => moveQuestionInList(current, fromIndex, toIndex))
  }

  const handleSaveReorder = async () => {
    const success = await persistQuestionOrder(reorderDraft)
    if (success) {
      setIsReorderModalOpen(false)
      setReorderDraft([])
    }
  }

  if (!quizId) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Card className="max-w-lg border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-700">Quiz not found</CardTitle>
            <CardDescription className="text-sm text-red-600">
              We could not find the quiz you are looking for.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  if (isQuizLoading || isQuestionsLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (quizError || !quiz) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Card className="max-w-lg border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-700">Unable to load quiz</CardTitle>
            <CardDescription className="text-sm text-red-600">
              The quiz may have been removed or you may not have permission to manage it.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  const quizMeta: QuizModel = quiz

  return (
    <div className="space-y-8">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <BackButton/>
            <p className="text-sm font-medium text-red-600">Quiz setup</p>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">{quizMeta.title}</h1>
          <p className="max-w-3xl text-gray-600">
            Add questions, update scoring, and keep learners informed about what to expect. When
            you are ready, share the quiz with your class.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" onClick={() => navigate(paths.app.quizzes.edit.build(quizMeta.id))}>
            Edit quiz details
          </Button>
        <Button variant="outline" onClick={() => navigate(paths.app.quizzes.attempts.list.build(quizMeta.id))}>
          Review attempts
        </Button>
          <Button className="flex items-center gap-2" onClick={handleCreateQuestion}>
            <Plus className="h-4 w-4" />
            Add question
          </Button>
        </div>
      </header>

      <section className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <SummaryTile label="Course" value={course?.title ?? 'Course details unavailable'} />
        <SummaryTile
          label="Availability"
          value={quizMeta.isActive ? 'Published for learners' : 'Hidden from learners'}
          tone={quizMeta.isActive ? 'success' : 'muted'}
        />
        <SummaryTile
          label="Total points"
          value={`${totalPoints} point${totalPoints === 1 ? '' : 's'}`}
        />
        <SummaryTile
          label="Time limit"
          value={
            quizMeta.timeLimitSeconds
              ? `${Math.round(quizMeta.timeLimitSeconds / 60)} minutes`
              : 'No timer'
          }
        />
        <SummaryTile
          label="Attempts allowed"
          value={quizMeta.maxAttempts ? `${quizMeta.maxAttempts}` : 'Single attempt'}
        />
        <SummaryTile
          label="Due date"
          value={
            quizMeta.dueDate ? new Date(quizMeta.dueDate).toLocaleString() : 'No due date set'
          }
        />
      </section>

      <section className="space-y-4">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Questions</h2>
            <p className="text-sm text-gray-600">
              Build a mix of question types to keep learners engaged. Set clear instructions for
              written and file upload responses.
            </p>
            {displayedQuestions.length > 1 && (
              <p className="text-xs text-gray-500">
                Use the Reorder button to adjust question order without leaving this page.
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {isSavingOrder && <span className="text-xs text-gray-500">Saving order…</span>}
            <Button
              variant="outline"
              className="flex items-center gap-2"
              onClick={handleOpenReorderModal}
              disabled={displayedQuestions.length < 2}
            >
              <ArrowUpDown className="h-4 w-4" />
              Reorder
            </Button>
            <Button onClick={handleCreateQuestion} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add question
            </Button>
          </div>
        </header>

        {displayedQuestions.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center gap-3 py-12 text-center">
              <Plus className="h-10 w-10 text-gray-300" />
              <p className="text-lg font-semibold text-gray-700">No questions yet</p>
              <p className="max-w-md text-sm text-gray-500">
                Start by adding your first question. You can mix multiple choice, written, numeric,
                and file upload formats.
              </p>
              <Button onClick={handleCreateQuestion} className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Add question
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {displayedQuestions.map((question, index) => (
              <Card key={question.id} className="border-gray-200 shadow-sm">
                <CardContent className="space-y-3 py-5">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="flex flex-wrap items-center gap-3">
                      <Badge variant="outline">Q{index + 1}</Badge>
                      <Badge variant="secondary">
                        {labelForQuizQuestionType(question.type)}
                      </Badge>
                      <Badge variant="outline">{question.scoreWeight ?? 0} pts</Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditQuestion(question)}
                        className="gap-1"
                      >
                        <Pencil className="h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1 text-red-600 hover:text-red-700"
                        onClick={() => setQuestionToDelete(question)}
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </Button>
                    </div>
                  </div>
                  <p className="text-gray-900">{question.questionText}</p>
                  <QuestionDetails question={question} />
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      <Modal
        isOpen={isQuestionModalOpen}
        onClose={() => {
          setIsQuestionModalOpen(false)
          setQuestionToEdit(null)
        }}
        title={questionToEdit ? 'Update question' : 'Add question'}
        size="xl"
      >
        <QuizQuestionForm
          mode={questionToEdit ? 'update' : 'create'}
          question={questionToEdit}
          isSubmitting={createQuestion.isPending || updateQuestion.isPending}
          onSubmit={handleSubmitQuestion}
          onCancel={() => {
            setIsQuestionModalOpen(false)
            setQuestionToEdit(null)
          }}
        />
      </Modal>

      <Modal
        isOpen={isReorderModalOpen}
        onClose={handleCloseReorderModal}
        title="Reorder questions"
        size="md"
      >
        {reorderDraft.length === 0 ? (
          <p className="text-sm text-gray-600">Add more questions to enable reordering.</p>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Use the controls to move questions up or down. Saving will update the learner view.
            </p>
            <ol className="space-y-3">
              {reorderDraft.map((question, index) => (
                <li
                  key={question.id}
                  className="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 px-4 py-3"
                >
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">Q{index + 1}</Badge>
                    <span className="text-sm text-gray-800">
                      {question.questionText || 'Untitled question'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMoveDraftQuestion(index, index - 1)}
                      disabled={index === 0 || isSavingOrder}
                      aria-label="Move question up"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMoveDraftQuestion(index, index + 1)}
                      disabled={index === reorderDraft.length - 1 || isSavingOrder}
                      aria-label="Move question down"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              ))}
            </ol>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={handleCloseReorderModal} disabled={isSavingOrder}>
                Cancel
              </Button>
              <Button onClick={handleSaveReorder} disabled={isSavingOrder}>
                {isSavingOrder ? 'Saving…' : 'Save order'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={Boolean(questionToDelete)}
        title="Remove question?"
        description={
          questionToDelete
            ? `Are you sure you want to remove "${questionToDelete.questionText}" from this quiz?`
            : ''
        }
        confirmLabel="Delete question"
        confirmTone="destructive"
        onCancel={() => setQuestionToDelete(null)}
        onConfirm={handleDeleteQuestion}
      />
    </div>
  )
}

function SummaryTile({
  label,
  value,
  tone = 'default',
}: {
  label: string
  value: string
  tone?: 'default' | 'success' | 'muted'
}) {
  const toneClasses =
    tone === 'success'
      ? 'border-green-200 bg-green-50 text-green-700'
      : tone === 'muted'
        ? 'border-gray-200 bg-gray-50 text-gray-600'
        : 'border-gray-200 bg-white text-gray-800'

  return (
    <Card className={toneClasses}>
      <CardContent className="space-y-1 py-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-current/80">{label}</p>
        <p className="text-base font-semibold text-current">{value}</p>
      </CardContent>
    </Card>
  )
}

function QuestionDetails({ question }: { question: QuizQuestion }) {
  if (question.type === 'mcq' || question.type === 'multi') {
    const correct =
      question.type === 'mcq'
        ? [question.correctAnswer]
        : Array.isArray(question.correctAnswer)
          ? question.correctAnswer
          : []
    return (
      <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
        <p className="mb-2 text-sm font-medium text-gray-700">Answer choices</p>
        <ul className="space-y-2 text-sm">
          {(question.options ?? []).map((option) => {
            const isCorrect = correct.includes(option.key)
            return (
              <li
                key={`${question.id}-${option.key}`}
                className="flex items-center justify-between rounded border border-gray-100 bg-white px-3 py-2"
              >
                <span className="flex items-center gap-3">
                  <Badge variant="outline">{option.key}</Badge>
                  <span>{option.text}</span>
                </span>
                {isCorrect && <Badge variant="default">Correct</Badge>}
              </li>
            )
          })}
        </ul>
      </div>
    )
  }

  if (question.type === 'numeric') {
    const answer = question.correctAnswer as { value?: number; tolerance?: number } | null
    return (
      <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
        Expected value: <strong>{answer?.value ?? 'Not set'}</strong>
        {answer?.tolerance ? (
          <span className="ml-2 text-xs text-blue-600">
            ± {answer.tolerance} acceptable difference
          </span>
        ) : null}
      </div>
    )
  }

  if (question.type === 'short_answer') {
    const metadata = question.metadata as any
    const rubric = Array.isArray(metadata?.rubric) ? metadata.rubric : []
    return (
      <div className="space-y-3 rounded-lg border border-purple-100 bg-purple-50 p-4 text-sm text-purple-700">
        {question.correctAnswer && (
          <p>
            Suggested response:{' '}
            <strong className="font-medium text-purple-800">
              {question.correctAnswer as string}
            </strong>
          </p>
        )}
        {metadata?.hint && (
          <p className="text-purple-600">
            Hint for learners: <em>{metadata.hint}</em>
          </p>
        )}
        {rubric.length > 0 && (
          <div className="rounded-md border border-purple-200 bg-white p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-purple-700">
              Grading checklist
            </p>
            <ul className="space-y-1 text-sm text-purple-700">
              {rubric.map((item: any, index: number) => (
                <li key={`rubric-${question.id}-${index}`} className="flex items-center justify-between">
                  <span>{item.criteria}</span>
                  <span className="font-semibold">+{item.points} pts</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )
  }

  if (question.type === 'file_upload') {
    const metadata = question.metadata as any
    return (
      <div className="rounded-lg border border-amber-100 bg-amber-50 p-4 text-sm text-amber-800">
        <p className="font-medium">Learners will upload files for review.</p>
        {metadata?.instructions && <p className="mt-1 text-amber-700">{metadata.instructions}</p>}
        <dl className="mt-3 grid gap-2 text-xs">
          {metadata?.maxFiles && (
            <div>
              <dt className="font-semibold uppercase tracking-wide text-amber-700">Max files</dt>
              <dd>{metadata.maxFiles}</dd>
            </div>
          )}
          {metadata?.maxFileSizeBytes && (
            <div>
              <dt className="font-semibold uppercase tracking-wide text-amber-700">Max size</dt>
              <dd>{Math.round(metadata.maxFileSizeBytes / 1000000)} MB per file</dd>
            </div>
          )}
          {Array.isArray(metadata?.allowedMimeTypes) && metadata.allowedMimeTypes.length > 0 && (
            <div>
              <dt className="font-semibold uppercase tracking-wide text-amber-700">Allowed types</dt>
              <dd>{metadata.allowedMimeTypes.join(', ')}</dd>
            </div>
          )}
        </dl>
      </div>
    )
  }

  return null
}

function normalizeQuestionOrder(questions: QuizQuestion[]): QuizQuestion[] {
  return questions
    .slice()
    .sort((a, b) => {
      const orderA = a.order ?? Number.MAX_SAFE_INTEGER
      const orderB = b.order ?? Number.MAX_SAFE_INTEGER
      if (orderA === orderB) {
        return a.questionText.localeCompare(b.questionText)
      }
      return orderA - orderB
    })
    .map((question, index) => ({
      ...question,
      order: index + 1,
    }))
}

function areQuestionsInSameOrder(a: QuizQuestion[], b: QuizQuestion[]): boolean {
  if (a === b) return true
  if (a.length !== b.length) return false
  for (let index = 0; index < a.length; index += 1) {
    const left = a[index]
    const right = b[index]
    if (!left || !right) return false
    if (left.id !== right.id) return false
    const leftOrder = left.order ?? index + 1
    const rightOrder = right.order ?? index + 1
    if (leftOrder !== rightOrder) return false
  }
  return true
}

function moveQuestionInList(list: QuizQuestion[], fromIndex: number, toIndex: number): QuizQuestion[] {
  if (fromIndex === toIndex) return applySequentialOrder(list)
  if (fromIndex < 0 || fromIndex >= list.length) return applySequentialOrder(list)
  if (toIndex < 0 || toIndex >= list.length) return applySequentialOrder(list)
  const working = list.slice()
  const [item] = working.splice(fromIndex, 1)
  working.splice(toIndex, 0, item)
  return applySequentialOrder(working)
}

function applySequentialOrder(list: QuizQuestion[]): QuizQuestion[] {
  return list.map((question, index) => ({
    ...question,
    order: index + 1,
  }))
}
